// Cache per evitare ricaricamenti
const loadedThemes = new Set<string>();
let currentTheme: string | undefined;

/**
 * Load a brand-specific theme with caching and optimization
 * @param brand - The brand theme to load
 * @returns Promise that resolves when theme is loaded
 */
export async function loadTheme(brand?: string): Promise<void> {
  const themeName = brand || "default";

  // Return immediately if already loaded
  if (loadedThemes.has(themeName) && currentTheme === themeName) {
    return;
  }

  try {
    // Load base theme first if not already loaded
    if (!loadedThemes.has("default")) {
      await import("@styles/themes/default-variables.css");
      loadedThemes.add("default");
    }

    // If requesting default, we're done
    if (themeName === "default") {
      currentTheme = "default";
      return;
    }

    // Load brand-specific theme in parallel
    await Promise.all([
      import(`@styles/themes/${themeName}/fonts.css`),
      import(`@styles/themes/${themeName}/variables.css`),
    ]);

    loadedThemes.add(themeName);
    currentTheme = themeName;

    if (import.meta.env.DEV) {
      console.log(`[Theme] Loaded: ${themeName}`);
    }
  } catch (error) {
    console.error(`[Theme] Failed to load: ${themeName}`, error);

    // Fallback to default if brand fails
    if (themeName !== "default" && !loadedThemes.has("default")) {
      await import("@styles/themes/default-variables.css");
      loadedThemes.add("default");
      currentTheme = "default";
    }
  }
}

/**
 * Preload a theme without blocking rendering
 * Useful for preloading themes that might be used later
 */
export function preloadTheme(brand?: string): void {
  loadTheme(brand).catch((err) => {
    console.warn(`[Theme] Preload failed for ${brand}:`, err);
  });
}

/**
 * Get the currently loaded theme
 */
export function getCurrentTheme(): string | undefined {
  return currentTheme;
}

/**
 * Clear theme cache (useful for testing)
 */
export function clearThemeCache(): void {
  loadedThemes.clear();
  currentTheme = undefined;
}

/**
 * Check if a theme is already loaded
 */
export function isThemeLoaded(brand: string): boolean {
  return loadedThemes.has(brand);
}
